export interface DocAsset {
  label: string;
  href: string;
  newTab?: boolean;
}

export interface DocEntry {
  id: string;
  title: string;
  summary: string;
  eyebrow: string;
  section: 'reference' | 'guide' | 'generated' | 'overview';
  routePath: string;
  contentPath: string;
  tags: string[];
  assets: DocAsset[];
}

export interface DeploymentReference {
  id: string;
  title: string;
  routePath: string;
  summary: string;
  diagramHref: string;
  flowHref?: string;
  tags: string[];
  highlights: string[];
}

export interface DiagramCard {
  id: string;
  title: string;
  description: string;
  href: string;
  routePath?: string;
  tags: string[];
}

export const docEntries: DocEntry[] = [
  {
    id: 'docs-readme',
    title: 'Docs README',
    summary:
      'Inventory of generated Mermaid, SVG, PNG, and explorer assets plus entry points into the architecture notes.',
    eyebrow: 'Docs index',
    section: 'overview',
    routePath: 'readme',
    contentPath: 'README.md',
    tags: ['Inventory', 'Generated assets'],
    assets: [
      { label: 'Raw README', href: '/content/README.md', newTab: true },
      { label: 'Explorer', href: '/explorer/' },
    ],
  },
  {
    id: 'system-overview',
    title: 'System Overview',
    summary:
      'High-level orientation to the platform, runtime maps, and the service relationships that matter most first.',
    eyebrow: 'Architecture',
    section: 'overview',
    routePath: 'architecture/system-overview',
    contentPath: 'architecture/system-overview.md',
    tags: ['Platform', 'Runtime'],
    assets: [
      {
        label: 'Topology SVG',
        href: '/diagrams/generated/service-topology.svg',
        newTab: true,
      },
      {
        label: 'Communication SVG',
        href: '/diagrams/generated/service-communication.svg',
        newTab: true,
      },
    ],
  },
  {
    id: 'deployment-architecture',
    title: 'Deployment Architecture',
    summary:
      'The cross-cutting deployment reference that ties the single-host, hosted Coolify, and multi-node models together.',
    eyebrow: 'Deployment',
    section: 'reference',
    routePath: 'architecture/deployment-architecture',
    contentPath: 'architecture/deployment-architecture.md',
    tags: ['Deployment', 'Reference'],
    assets: [
      {
        label: 'Deployment order',
        href: '/diagrams/generated/deployment-order.svg',
        newTab: true,
      },
      { label: 'Architecture hub', href: '/architecture' },
    ],
  },
  {
    id: 'single-server',
    title: 'Single-server Coolify Deployment',
    summary:
      'The smallest production-friendly layout: one Coolify-managed host, one proxy layer, one operational blast radius.',
    eyebrow: 'Deployment',
    section: 'reference',
    routePath: 'architecture/single-server-coolify-deployment',
    contentPath: 'architecture/single-server-coolify-deployment.md',
    tags: ['Deployment', 'Coolify', 'Single host'],
    assets: [
      {
        label: 'Open topology',
        href: '/diagrams/generated/single-server-coolify-deployment.svg',
        newTab: true,
      },
    ],
  },
  {
    id: 'hosted-ubuntu',
    title: 'Ubuntu + Coolify Hosted Deployment',
    summary:
      'A split build/runtime model where Forgejo, Coolify, the build server, registry, migration job, and runtime host each have a clear role.',
    eyebrow: 'Deployment',
    section: 'reference',
    routePath: 'architecture/ubuntu-coolify-deployment',
    contentPath: 'architecture/ubuntu-coolify-deployment.md',
    tags: ['Deployment', 'Coolify', 'Build server'],
    assets: [
      {
        label: 'Topology SVG',
        href: '/diagrams/generated/ubuntu-coolify-deployment.svg',
        newTab: true,
      },
      {
        label: 'Flow SVG',
        href: '/diagrams/generated/ubuntu-coolify-deployment-flow.svg',
        newTab: true,
      },
      {
        label: 'Flow source',
        href: '/diagrams/generated/ubuntu-coolify-deployment-flow.mmd',
        newTab: true,
      },
    ],
  },
  {
    id: 'multi-node',
    title: 'Multi-node Coolify + SurrealDB Deployment',
    summary:
      'Load-balanced multi-server model: SSL termination at the edge, round-robin to identical servers, clustered SurrealDB data tier.',
    eyebrow: 'Deployment',
    section: 'reference',
    routePath: 'architecture/multi-node-coolify-surrealdb-deployment',
    contentPath: 'architecture/multi-node-coolify-surrealdb-deployment.md',
    tags: ['Deployment', 'Coolify', 'Multi-node', 'Load balancer'],
    assets: [
      {
        label: 'Topology SVG',
        href: '/diagrams/generated/multi-node-coolify-surrealdb-deployment.svg',
        newTab: true,
      },
      {
        label: 'Flow SVG',
        href: '/diagrams/generated/multi-node-coolify-surrealdb-flow.svg',
        newTab: true,
      },
      {
        label: 'Flow source',
        href: '/diagrams/generated/multi-node-coolify-surrealdb-flow.mmd',
        newTab: true,
      },
    ],
  },
  {
    id: 'stand-in-platform',
    title: 'Stand-in Platform',
    summary:
      'A small, independent fallback on a second provider: Delta record create, edit and find by number, plus static 15-minute Pulse reports, while the primary is down.',
    eyebrow: 'Resilience',
    section: 'reference',
    routePath: 'architecture/stand-in-platform',
    contentPath: 'architecture/stand-in-platform.md',
    tags: ['Resilience', 'Disaster recovery', 'Events'],
    assets: [{ label: 'Architecture hub', href: '/architecture' }],
  },
  {
    id: 'pulse-public-reporting',
    title: 'Pulse Public Reporting',
    summary:
      'What Pulse can publish without sign-in, what publishing exposes, how an item is taken back, and the rate limits, row caps and cache rules on the public surface.',
    eyebrow: 'Pulse',
    section: 'reference',
    routePath: 'architecture/pulse-public-reporting',
    contentPath: 'architecture/pulse-public-reporting.md',
    tags: ['Pulse', 'Public access', 'Disclosure control'],
    assets: [{ label: 'Architecture hub', href: '/architecture' }],
  },
  {
    id: 'auth-runtime',
    title: 'Generated Auth Runtime',
    summary:
      'Source-driven notes for the auth service, Better Auth modules, and the live auth flow diagrams generated from the app itself.',
    eyebrow: 'Generated',
    section: 'generated',
    routePath: 'architecture/generated/auth-runtime',
    contentPath: 'architecture/generated/auth-runtime.md',
    tags: ['Auth', 'Generated'],
    assets: [
      {
        label: 'Auth flow SVG',
        href: '/diagrams/generated/auth-flow.svg',
        newTab: true,
      },
      { label: 'Explorer', href: '/explorer/' },
    ],
  },
  {
    id: 'records-service-auth-integration',
    title: 'Generated Forms-Service Auth Integration',
    summary:
      'A source-driven trace from authClient.token() through records-service middleware and into SurrealDB authenticate(token).',
    eyebrow: 'Generated',
    section: 'generated',
    routePath: 'architecture/generated/records-service-auth-integration',
    contentPath: 'architecture/generated/records-service-auth-integration.md',
    tags: ['Auth', 'Forms', 'Surreal', 'Generated'],
    assets: [
      {
        label: 'Forms auth flow SVG',
        href: '/diagrams/generated/records-service-auth-flow.svg',
        newTab: true,
      },
      {
        label: 'Flow source',
        href: '/diagrams/generated/records-service-auth-flow.mmd',
        newTab: true,
      },
      { label: 'Auth runtime', href: '/architecture/generated/auth-runtime' },
    ],
  },
  {
    id: 'publishing-guide',
    title: 'Publishing the Docs Site',
    summary:
      'Build and deploy the docs frontend and image so the latest generated architecture assets can be hosted publicly.',
    eyebrow: 'Guide',
    section: 'guide',
    routePath: 'guides/publishing-docs',
    contentPath: 'guides/publishing-docs.md',
    tags: ['Guide', 'Docker'],
    assets: [
      {
        label: 'Raw guide',
        href: '/content/guides/publishing-docs.md',
        newTab: true,
      },
    ],
  },
  {
    id: 'railroad-diagrams-guide',
    title: 'Railroad Diagrams',
    summary:
      'How to write fenced railroad blocks in markdown and let docs-site render them as grammar-style SVG diagrams.',
    eyebrow: 'Guide',
    section: 'guide',
    routePath: 'guides/railroad-diagrams',
    contentPath: 'guides/railroad-diagrams.md',
    tags: ['Guide', 'Syntax', 'Railroad'],
    assets: [
      {
        label: 'Raw guide',
        href: '/content/guides/railroad-diagrams.md',
        newTab: true,
      },
    ],
  },
];

export const deploymentReferences: DeploymentReference[] = [
  {
    id: 'single-server',
    title: 'Single-server Coolify',
    routePath: 'architecture/single-server-coolify-deployment',
    summary:
      'Best when one host can still comfortably own the proxy, services, jobs, and database without becoming a space heater with feelings.',
    diagramHref: '/diagrams/generated/single-server-coolify-deployment.svg',
    tags: ['Coolify', 'Single host'],
    highlights: [
      'One public ingress and one operational box to reason about.',
      'Fastest path to production when the platform still fits on a single machine.',
      'Simplest rollout and rollback model when you want fewer moving pieces.',
    ],
  },
  {
    id: 'hosted-ubuntu',
    title: 'Hosted Ubuntu + Coolify',
    routePath: 'architecture/ubuntu-coolify-deployment',
    summary:
      'Build once on a dedicated Coolify build server, publish immutable images to the registry, then let the runtime host pull and roll forward.',
    diagramHref: '/diagrams/generated/ubuntu-coolify-deployment.svg',
    flowHref: '/diagrams/generated/ubuntu-coolify-deployment-flow.svg',
    tags: ['Coolify', 'Build server', 'Registry'],
    highlights: [
      'Clear split between delivery responsibilities and runtime responsibilities.',
      'Migration job runs ahead of dependent workloads during rollout.',
      'A great default when build pressure hurts more than runtime scale today.',
    ],
  },
  {
    id: 'multi-node',
    title: 'Multi-node Scale-out',
    routePath: 'architecture/multi-node-coolify-surrealdb-deployment',
    summary:
      'External load balancer terminates SSL and round-robins to identical servers. Every server runs the same domains and apps behind its own Traefik proxy.',
    diagramHref:
      '/diagrams/generated/multi-node-coolify-surrealdb-deployment.svg',
    flowHref: '/diagrams/generated/multi-node-coolify-surrealdb-flow.svg',
    tags: ['Coolify', 'Multi-node', 'SurrealDB', 'Load balancer'],
    highlights: [
      'Load balancer terminates SSL — everything after is HTTP via round-robin.',
      'Every server is identical: same domains, same apps, same Traefik proxy.',
      'Scale by adding servers behind the LB. No domain reshuffling required.',
      'Health-check on /ping drains unhealthy nodes automatically.',
    ],
  },
];

export const diagramCards: DiagramCard[] = [
  {
    id: 'service-topology',
    title: 'Service Topology',
    description:
      'The runtime map of frontends, APIs, backing services, ports, and persistent volumes across the monorepo.',
    href: '/diagrams/generated/service-topology.svg',
    routePath: 'architecture/system-overview',
    tags: ['Runtime', 'Generated'],
  },
  {
    id: 'service-communication',
    title: 'Communication Map',
    description:
      'HTTP and WebSocket relationships between frontends, Better Auth, APIs, and the supporting services.',
    href: '/diagrams/generated/service-communication.svg',
    routePath: 'architecture/system-overview',
    tags: ['Traffic', 'Generated'],
  },
  {
    id: 'auth-flow',
    title: 'Auth Flow',
    description:
      'Source-driven Better Auth modules, middleware, and the end-to-end auth request path generated from the auth app.',
    href: '/diagrams/generated/auth-flow.svg',
    routePath: 'architecture/generated/auth-runtime',
    tags: ['Auth', 'Generated'],
  },
  {
    id: 'records-service-auth-flow',
    title: 'Forms-Service Auth Flow',
    description:
      'Shows how a Better Auth JWT moves from the browser into records-service middleware and then into SurrealDB authenticate(token).',
    href: '/diagrams/generated/records-service-auth-flow.svg',
    routePath: 'architecture/generated/records-service-auth-integration',
    tags: ['Auth', 'Forms', 'Surreal'],
  },
  {
    id: 'deployment-order',
    title: 'Deployment Order',
    description:
      'The rollout sequencing view that helps explain how migrations, runtimes, and frontends come up without stepping on each other.',
    href: '/diagrams/generated/deployment-order.svg',
    routePath: 'architecture/deployment-architecture',
    tags: ['Deployment', 'Generated'],
  },
  {
    id: 'hosted-flow',
    title: 'Hosted Deployment Flow',
    description:
      'A flow-first view of Forgejo, Coolify, the build server, the registry, and the runtime host working in sequence.',
    href: '/diagrams/generated/ubuntu-coolify-deployment-flow.svg',
    routePath: 'architecture/ubuntu-coolify-deployment',
    tags: ['Flow', 'Hosted'],
  },
  {
    id: 'multi-node-flow',
    title: 'Multi-node Deployment Flow',
    description:
      'Build-once-deploy-everywhere flow with load balancer round-robin and per-server Traefik routing.',
    href: '/diagrams/generated/multi-node-coolify-surrealdb-flow.svg',
    routePath: 'architecture/multi-node-coolify-surrealdb-deployment',
    tags: ['Flow', 'Multi-node', 'LB'],
  },
];

export interface MermaidSource {
  id: string;
  title: string;
  description: string;
  mmdHref: string;
  svgHref: string;
  pngHref: string;
  tags: string[];
}

export const mermaidSources: MermaidSource[] = [
  {
    id: 'auth-flow',
    title: 'Auth Flow',
    description:
      'End-to-end Better Auth request path from login through middleware.',
    mmdHref: '/diagrams/generated/auth-flow.mmd',
    svgHref: '/diagrams/generated/auth-flow.svg',
    pngHref: '/diagrams/generated/auth-flow.png',
    tags: ['Auth', 'Sequence'],
  },
  {
    id: 'dependency-graph',
    title: 'Dependency Graph',
    description: 'Package dependency relationships across the monorepo.',
    mmdHref: '/diagrams/generated/dependency-graph.mmd',
    svgHref: '/diagrams/generated/dependency-graph.svg',
    pngHref: '/diagrams/generated/dependency-graph.png',
    tags: ['Packages', 'Graph'],
  },
  {
    id: 'deployment-order',
    title: 'Deployment Order',
    description: 'Rollout sequencing for migrations, runtimes, and frontends.',
    mmdHref: '/diagrams/generated/deployment-order.mmd',
    svgHref: '/diagrams/generated/deployment-order.svg',
    pngHref: '/diagrams/generated/deployment-order.png',
    tags: ['Deployment', 'Flow'],
  },
  {
    id: 'records-service-auth-flow',
    title: 'Forms-Service Auth Flow',
    description:
      'JWT path from browser through records-service into SurrealDB.',
    mmdHref: '/diagrams/generated/records-service-auth-flow.mmd',
    svgHref: '/diagrams/generated/records-service-auth-flow.svg',
    pngHref: '/diagrams/generated/records-service-auth-flow.png',
    tags: ['Auth', 'Forms', 'Surreal'],
  },
  {
    id: 'frontend-backend-map',
    title: 'Frontend-Backend Map',
    description: 'Which frontends talk to which backend services.',
    mmdHref: '/diagrams/generated/frontend-backend-map.mmd',
    svgHref: '/diagrams/generated/frontend-backend-map.svg',
    pngHref: '/diagrams/generated/frontend-backend-map.png',
    tags: ['Traffic', 'Map'],
  },
  {
    id: 'multi-node-coolify-surrealdb-deployment',
    title: 'Multi-node Deployment',
    description:
      'Load-balanced multi-server topology with SSL termination and round-robin.',
    mmdHref: '/diagrams/generated/multi-node-coolify-surrealdb-deployment.mmd',
    svgHref: '/diagrams/generated/multi-node-coolify-surrealdb-deployment.svg',
    pngHref: '/diagrams/generated/multi-node-coolify-surrealdb-deployment.png',
    tags: ['Multi-node', 'Deployment', 'LB'],
  },
  {
    id: 'multi-node-coolify-surrealdb-flow',
    title: 'Multi-node Flow',
    description:
      'Build-once-deploy-everywhere flow with LB round-robin runtime path.',
    mmdHref: '/diagrams/generated/multi-node-coolify-surrealdb-flow.mmd',
    svgHref: '/diagrams/generated/multi-node-coolify-surrealdb-flow.svg',
    pngHref: '/diagrams/generated/multi-node-coolify-surrealdb-flow.png',
    tags: ['Multi-node', 'Flow', 'LB'],
  },
  {
    id: 'service-communication',
    title: 'Service Communication',
    description: 'HTTP and WebSocket relationships between all services.',
    mmdHref: '/diagrams/generated/service-communication.mmd',
    svgHref: '/diagrams/generated/service-communication.svg',
    pngHref: '/diagrams/generated/service-communication.png',
    tags: ['Traffic', 'Runtime'],
  },
  {
    id: 'service-topology',
    title: 'Service Topology',
    description: 'Runtime map of ports, volumes, and service relationships.',
    mmdHref: '/diagrams/generated/service-topology.mmd',
    svgHref: '/diagrams/generated/service-topology.svg',
    pngHref: '/diagrams/generated/service-topology.png',
    tags: ['Runtime', 'Topology'],
  },
  {
    id: 'single-server-coolify-deployment',
    title: 'Single-server Deployment',
    description: 'Single-host Coolify deployment with proxy, services, and DB.',
    mmdHref: '/diagrams/generated/single-server-coolify-deployment.mmd',
    svgHref: '/diagrams/generated/single-server-coolify-deployment.svg',
    pngHref: '/diagrams/generated/single-server-coolify-deployment.png',
    tags: ['Coolify', 'Deployment'],
  },
  {
    id: 'ubuntu-coolify-deployment',
    title: 'Hosted Ubuntu Deployment',
    description: 'Build-server → registry → runtime host deployment topology.',
    mmdHref: '/diagrams/generated/ubuntu-coolify-deployment.mmd',
    svgHref: '/diagrams/generated/ubuntu-coolify-deployment.svg',
    pngHref: '/diagrams/generated/ubuntu-coolify-deployment.png',
    tags: ['Hosted', 'Deployment'],
  },
  {
    id: 'ubuntu-coolify-deployment-flow',
    title: 'Hosted Ubuntu Flow',
    description: 'Sequential flow from Forgejo to Coolify to runtime host.',
    mmdHref: '/diagrams/generated/ubuntu-coolify-deployment-flow.mmd',
    svgHref: '/diagrams/generated/ubuntu-coolify-deployment-flow.svg',
    pngHref: '/diagrams/generated/ubuntu-coolify-deployment-flow.png',
    tags: ['Hosted', 'Flow'],
  },
];

export function normalizeContentPath(path: string) {
  return path.replace(/\\/g, '/').replace(/^\.\//, '').replace(/^\/+/, '');
}

function normalizeRoutePath(routePath: string) {
  return routePath.replace(/^\/+/, '').replace(/\/+$/, '');
}

export function findDocByRoute(routePath: string) {
  const normalized = normalizeRoutePath(routePath);
  return docEntries.find((doc) => doc.routePath === normalized);
}

export function findDocByContent(contentPath: string) {
  const normalized = normalizeContentPath(contentPath);
  return docEntries.find(
    (doc) => normalizeContentPath(doc.contentPath) === normalized,
  );
}

export function getRelatedDocs(entry: DocEntry) {
  return docEntries
    .filter((doc) => doc.id !== entry.id)
    .filter(
      (doc) =>
        doc.section === entry.section ||
        doc.tags.some((tag) => entry.tags.includes(tag)),
    )
    .slice(0, 4);
}

export function getPageMeta(routePath: string) {
  const normalized = normalizeRoutePath(routePath);

  if (normalized === '') {
    return {
      title: 'Crown Docs',
      subtitle: 'Overview',
    };
  }

  if (normalized === 'architecture') {
    return {
      title: 'Architecture Hub',
      subtitle: 'Deployment references, generated maps, and explorer shortcuts',
    };
  }

  if (normalized === 'dsl-playground') {
    return {
      title: 'Crown DSL Playground',
      subtitle:
        'Generate and validate Crown DSL from plain English via the production crown-dsl model',
    };
  }

  const doc = findDocByRoute(normalized);
  if (doc) {
    return {
      title: doc.title,
      subtitle: doc.summary,
    };
  }

  return {
    title: 'Crown Docs',
    subtitle: 'Source-driven architecture and deployment references',
  };
}
